import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import Refresh from '@mui/icons-material/Refresh';

import { useState, useEffect, useCallback } from 'react';

import * as api from '../../api.js';
import { useAuth } from '../../AuthContext';

export const FailedSales = () => {
    const [failedSales, setFailedSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [retryDialog, setRetryDialog] = useState(null);
    const [retryPaymentMethod, setRetryPaymentMethod] = useState('card');
    const [retryCashReceived, setRetryCashReceived] = useState('');
    const [retrying, setRetrying] = useState(false);

    const { token, claims } = useAuth();

    const loadFailedSales = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const sales = await api.getFailedSales(token);
            setFailedSales(sales);
        } catch (err) {
            setError('Failed to load failed sales');
        } finally {
            setLoading(false);
        }
    }, [ token ]);

    useEffect(() => {
        loadFailedSales();
    }, [ loadFailedSales ]);
    
    // Check if user is admin
    if (!claims?.admin) {
        return (
            <Container maxWidth="md" sx={{ mt: 4 }}>
                <Alert severity="error">
                    Access denied. Failed sales management is only available to administrators.
                </Alert>
            </Container>
        );
    }

    const handleRetry = (sale) => {
        setRetryDialog(sale);
        setRetryPaymentMethod('card');
        setRetryCashReceived('');
    };

    const handleRetryConfirm = async () => {
        if (!retryDialog) return;

        try {
            setRetrying(true);

            const retryData = {
                paymentMethod: retryPaymentMethod
            };

            if (retryPaymentMethod === 'cash') {
                const received = parseFloat(retryCashReceived);
                if (isNaN(received)) {
                    setError('Invalid cash amount');
                    return;
                }
                retryData.cashReceived = received;
            }

            await api.retryFailedSale(retryDialog.id, retryData, token);

            setRetryDialog(null);
            loadFailedSales(); // Refresh the list

        } catch (err) {
            setError(err.message || 'Retry failed');
        } finally {
            setRetrying(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    const getTotal = (items) => {
        return Object.values(items).reduce((total, item) => total + (item.price * item.quantity), 0);
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">
                    Failed Sales Management
                </Typography>
                <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={loadFailedSales}
                    disabled={loading}
                >
                    Refresh
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {loading ? (
                <Typography>Loading...</Typography>
            ) : failedSales.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h6" color="text.secondary">
                        No failed sales to process
                    </Typography>
                </Paper>
            ) : (
                <Grid container spacing={2}>
                    {failedSales.map((sale) => (
                        <Grid item xs={12} md={6} lg={4} key={sale.id}>
                            <Card>
                                <CardHeader
                                    title={`Sale ${sale.id}`}
                                    subheader={formatDate(sale.createdAt)}
                                    action={
                                        <Chip
                                            label="Failed"
                                            color="error"
                                            size="small"
                                        />
                                    }
                                />
                                <CardContent>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Error: {sale.error}
                                    </Typography>

                                    <Typography variant="body1" sx={{ mb: 1 }}>
                                        Total: ${getTotal(sale.items).toFixed(2)}
                                    </Typography>

                                    <Typography variant="body2" sx={{ mb: 2 }}>
                                        Items: {Object.keys(sale.items).length}
                                    </Typography>

                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={() => handleRetry(sale)}
                                            fullWidth
                                        >
                                            Retry Payment
                                        </Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Retry Dialog */}
            <Dialog open={!!retryDialog} onClose={() => setRetryDialog(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Retry Failed Sale</DialogTitle>
                <DialogContent>
                    {retryDialog && (
                        <Box sx={{ pt: 1 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Sale ID: {retryDialog.id}
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                                Total: ${getTotal(retryDialog.items).toFixed(2)}
                            </Typography>

                            <TextField
                                select
                                fullWidth
                                label="Payment Method"
                                value={retryPaymentMethod}
                                onChange={(e) => setRetryPaymentMethod(e.target.value)}
                                SelectProps={{ native: true }}
                                sx={{ mt: 2 }}
                            >
                                <option value="card">Card</option>
                                <option value="cash">Cash</option>
                            </TextField>

                            {retryPaymentMethod === 'cash' && (
                                <TextField
                                    fullWidth
                                    label="Cash Received"
                                    type="number"
                                    step="0.01"
                                    value={retryCashReceived}
                                    onChange={(e) => setRetryCashReceived(e.target.value)}
                                    placeholder="0.00"
                                    sx={{ mt: 2 }}
                                />
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRetryDialog(null)}>Cancel</Button>
                    <Button
                        onClick={handleRetryConfirm}
                        variant="contained"
                        disabled={retrying}
                    >
                        {retrying ? 'Processing...' : 'Retry Sale'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};